import dayjs from 'dayjs';
import { findScopeIncludeExcludeConflicts } from '@evimo/core/modules/partner-maintenance-rules/domain/partner-maintenance-scope-effect-conflict.utils';
import { findScopeIncludeHierarchyRedundancies } from '@evimo/core/modules/partner-maintenance-rules/domain/partner-maintenance-scope-hierarchy.utils';
import { SlaContractStatus } from '@evimo/types/enums/sla-contract-status';
import {
  MAINTENANCE_SLA_CALENDAR_MODES,
  MAINTENANCE_SLA_CLOCK_STARTS,
  MAINTENANCE_SLA_LEVEL_CODES,
  MAINTENANCE_SLA_ON_SITE_LEVELS,
  MAINTENANCE_SLA_REMOTE_LEVELS,
  MAINTENANCE_SLA_SCOPE_DIMENSIONS,
  MAINTENANCE_SLA_SCOPE_EFFECTS,
  MAINTENANCE_SLA_SCOPE_INTERVENTION_MODES,
  MAINTENANCE_SLA_TIMER_KINDS,
  MAINTENANCE_TIMEZONES,
  type MaintenanceContractIssue,
  type MaintenanceContractIssueCode,
  type MaintenanceRulesStatus,
  type MaintenanceSlaCalendarMode,
  type MaintenanceSlaCategory,
  type MaintenanceSlaClockStart,
  type MaintenancePartnershipValidity,
  type MaintenanceSlaContractRecord,
  type MaintenanceSlaDraft,
  type MaintenanceSlaDraftGroup,
  type MaintenanceSlaDraftLevel,
  type MaintenanceSlaDraftPricingCombination,
  type MaintenanceSlaDraftScopeRule,
  type MaintenanceSlaScopeHierarchyAsset,
  type MaintenanceSlaDraftTerm,
  type MaintenanceSlaDraftTimer,
  EVIMO_RESOLUTION_STOP_SIGNAL,
  type MaintenanceSlaInterventionMode,
  type MaintenanceSlaLevelCode,
  type MaintenanceSlaPriority,
  type MaintenanceSlaSavedLevel,
  type MaintenanceSlaSavedPricingCombination,
  type MaintenanceSlaSavedTimer,
  type MaintenanceSlaScopeInterventionMode,
  type MaintenanceSlaTimerKind,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

import {
  durationPartsToMinutes,
  minutesToDurationParts,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/rules';

const LIVE_ISSUE_CODES = new Set<MaintenanceContractIssueCode>([
  'scopeHierarchyRedundancy',
  'scopeEffectConflict',
]);

export function toPartnershipValidity(contract: {
  status: string;
  validFrom: string | Date;
  validTo?: string | Date | null;
}): MaintenancePartnershipValidity {
  return {
    status: contract.status as SlaContractStatus,
    validFrom: contract.validFrom,
    validTo: contract.validTo ?? null,
  };
}

function toStoredDate(value: string | Date): string {
  return dayjs(value).toISOString();
}

export function createMaintenanceRowId(): string {
  return crypto.randomUUID();
}

export function levelsForCategory(
  category: MaintenanceSlaCategory,
): readonly MaintenanceSlaLevelCode[] {
  return category === 'ON_SITE'
    ? MAINTENANCE_SLA_ON_SITE_LEVELS
    : MAINTENANCE_SLA_REMOTE_LEVELS;
}

export function applicableLevelCodes(
  mode: MaintenanceSlaInterventionMode | null,
): readonly MaintenanceSlaLevelCode[] {
  if (mode === 'ONSITE') {
    return MAINTENANCE_SLA_ON_SITE_LEVELS;
  }
  if (mode === 'REMOTE') {
    return MAINTENANCE_SLA_REMOTE_LEVELS;
  }
  return [...MAINTENANCE_SLA_ON_SITE_LEVELS, ...MAINTENANCE_SLA_REMOTE_LEVELS];
}

export function createFixedLevelsForInterventionMode(
  mode: MaintenanceSlaInterventionMode | null,
): MaintenanceSlaDraftLevel[] {
  return applicableLevelCodes(mode).map((code) => createEmptyLevel(code));
}

export function minutesOutOfRange(minutes: number | null): boolean {
  return (
    minutes != null &&
    (!Number.isInteger(minutes) || minutes < 0 || minutes > 59)
  );
}

export function blankScopeValueBlocksSave(rule: { values: string[] }): boolean {
  return rule.values.filter((v) => v.trim().length > 0).length === 0;
}

export function isMaintenanceIssueVisible(
  code: MaintenanceContractIssueCode,
  showErrors: boolean,
): boolean {
  return showErrors || LIVE_ISSUE_CODES.has(code);
}

export function toNeutralSelectOptions(values: readonly string[]) {
  return values.map((value) => ({
    value,
    color: '#94a3b8',
    labelKey: value,
  }));
}

export const MAINTENANCE_CLOCK_START_OPTIONS = toNeutralSelectOptions(
  MAINTENANCE_SLA_CLOCK_STARTS,
);

export const MAINTENANCE_CALENDAR_MODE_OPTIONS = toNeutralSelectOptions(
  MAINTENANCE_SLA_CALENDAR_MODES.filter(
    (mode) => mode !== 'EXCLUDING_WEEKENDS_AND_HOLIDAYS',
  ),
);

export const MAINTENANCE_SCOPE_DIMENSION_OPTIONS = toNeutralSelectOptions(
  MAINTENANCE_SLA_SCOPE_DIMENSIONS,
);

export const MAINTENANCE_SCOPE_EFFECT_OPTIONS = toNeutralSelectOptions(
  MAINTENANCE_SLA_SCOPE_EFFECTS,
);

export const MAINTENANCE_TIMEZONE_OPTIONS = MAINTENANCE_TIMEZONES.map(
  (value) => ({
    value,
    label: value,
  }),
);

export function createEmptyTimers(): MaintenanceSlaDraftTimer[] {
  return MAINTENANCE_SLA_TIMER_KINDS.map((kind) => ({
    included: true,
    kind,
    clockStart: null,
    calendarMode: null,
  }));
}

export function createEmptyPricingCombination(): MaintenanceSlaDraftPricingCombination {
  return {
    id: createMaintenanceRowId(),
    takeChargeMinutes: null,
    interventionMinutes: null,
    resolutionMinutes: null,
    priceAmount: null,
  };
}

export function createEmptyTerm(
  priority: MaintenanceSlaPriority,
): MaintenanceSlaDraftTerm {
  return {
    priority,
    penaltyClauseText: '',
    penaltyAmount: null,
    timers: createEmptyTimers(),
    pricingCombinations: [],
  };
}

export function createEmptyGroup(
  category: MaintenanceSlaCategory,
): MaintenanceSlaDraftGroup {
  return {
    id: createMaintenanceRowId(),
    category,
    name: '',
    levels: [],
    standard: createEmptyTerm('STANDARD'),
    emergency: null,
  };
}

export function createEmptyScopeRule(): MaintenanceSlaDraftScopeRule {
  return {
    id: createMaintenanceRowId(),
    dimension: 'MANUFACTURER',
    effect: 'INCLUDE',
    values: [],
    interventionMode: null,
  };
}

export function isScopeInterventionModeValid(
  mode: MaintenanceSlaScopeInterventionMode | null,
): mode is MaintenanceSlaScopeInterventionMode {
  return (
    mode != null &&
    (MAINTENANCE_SLA_SCOPE_INTERVENTION_MODES as readonly string[]).includes(
      mode,
    )
  );
}

export function normalizeScopeRuleInterventionMode(
  rule: MaintenanceSlaDraftScopeRule,
  contractInterventionMode: MaintenanceSlaInterventionMode | null,
): MaintenanceSlaScopeInterventionMode | null {
  if (contractInterventionMode !== 'BOTH') {
    return null;
  }
  return isScopeInterventionModeValid(rule.interventionMode)
    ? rule.interventionMode
    : null;
}

export function createEmptyMaintenanceSlaDraft(
  mode: MaintenanceSlaInterventionMode = 'BOTH',
): MaintenanceSlaDraft {
  return {
    timezone: 'Europe/Paris',
    businessHoursStart: null,
    businessHoursEnd: null,
    workingDays: [],
    holidayCalendarCode: 'FR',
    exclusionsText: '',
    recurrenceWindowDays: null,
    restoredAvailableHours: null,
    restoredAvailableMinutes: null,
    priceCurrency: 'EUR',
    excludeHolidays: false,
    levels: createFixedLevelsForInterventionMode(mode),
    scopeRules: [],
    interventionMode: mode,
  };
}

function createEmptyLevel(
  code: MaintenanceSlaLevelCode,
): MaintenanceSlaDraftLevel {
  return {
    code,
    penaltyClauseText: '',
    penaltyAmount: null,
    timers: createEmptyTimers(),
    pricingCombinations: [],
  };
}

export function createFixedLevels(): MaintenanceSlaDraftLevel[] {
  return MAINTENANCE_SLA_LEVEL_CODES.map((code) => createEmptyLevel(code));
}

export function withLevelTimer(
  level: MaintenanceSlaDraftLevel,
  kind: MaintenanceSlaTimerKind,
  patch: Partial<MaintenanceSlaDraftTimer>,
): MaintenanceSlaDraftLevel {
  return {
    ...level,
    timers: level.timers.map((timer) =>
      timer.kind === kind ? { ...timer, ...patch } : timer,
    ),
  };
}

export function addPricingCombination(
  level: MaintenanceSlaDraftLevel,
): MaintenanceSlaDraftLevel {
  return {
    ...level,
    pricingCombinations: [
      ...level.pricingCombinations,
      createEmptyPricingCombination(),
    ],
  };
}

export function removePricingCombination(
  level: MaintenanceSlaDraftLevel,
  combinationId: string,
): MaintenanceSlaDraftLevel {
  return {
    ...level,
    pricingCombinations: level.pricingCombinations.filter(
      (combo) => combo.id !== combinationId,
    ),
  };
}

export function withPricingCombination(
  level: MaintenanceSlaDraftLevel,
  combinationId: string,
  patch: Partial<MaintenanceSlaDraftPricingCombination>,
): MaintenanceSlaDraftLevel {
  return {
    ...level,
    pricingCombinations: level.pricingCombinations.map((combo) =>
      combo.id === combinationId ? { ...combo, ...patch } : combo,
    ),
  };
}

export function propagateLevelValues(
  levels: MaintenanceSlaDraftLevel[],
  source: MaintenanceSlaDraftLevel,
): MaintenanceSlaDraftLevel[] {
  return levels.map((level) => {
    if (level.code === source.code) {
      return level;
    }

    let next = level;

    next = {
      ...next,
      timers: next.timers.map((timer) => {
        const sourceTimer = source.timers.find((t) => t.kind === timer.kind);
        if (!sourceTimer) {
          return timer;
        }
        return {
          ...timer,
          clockStart: timer.clockStart ?? sourceTimer.clockStart,
          calendarMode: timer.calendarMode ?? sourceTimer.calendarMode,
        };
      }),
    };

    next = {
      ...next,
      penaltyAmount: next.penaltyAmount ?? source.penaltyAmount,
      penaltyClauseText: next.penaltyClauseText || source.penaltyClauseText,
    };

    if (
      next.pricingCombinations.length === 0 &&
      source.pricingCombinations.length > 0
    ) {
      next = {
        ...next,
        pricingCombinations: source.pricingCombinations.map((combo) => ({
          ...combo,
          id: createMaintenanceRowId(),
        })),
      };
    }

    return next;
  });
}

export function withGroupLevelCodes(
  group: MaintenanceSlaDraftGroup,
  codes: readonly MaintenanceSlaLevelCode[],
): MaintenanceSlaDraftGroup {
  const levels = levelsForCategory(group.category)
    .filter((code) => codes.includes(code))
    .map((code) => {
      const existing = group.levels.find((level) => level.code === code);
      return existing ?? createEmptyLevel(code);
    });

  return { ...group, levels };
}

export function withTimer(
  term: MaintenanceSlaDraftTerm,
  kind: MaintenanceSlaTimerKind,
  patch: Partial<MaintenanceSlaDraftTimer>,
): MaintenanceSlaDraftTerm {
  return {
    ...term,
    timers: term.timers.map((timer) =>
      timer.kind === kind ? { ...timer, ...patch } : timer,
    ),
  };
}

function collectLevelTimerIssues(
  issues: MaintenanceContractIssue[],
  level: MaintenanceSlaDraftLevel,
) {
  for (const timer of level.timers) {
    const match = {
      levelCode: level.code,
      kind: timer.kind,
    };

    if (!timer.clockStart) {
      issues.push({ code: 'timerClock', ...match });
    }

    if (!timer.calendarMode) {
      issues.push({ code: 'timerCalendar', ...match });
    }
  }
}

function collectLevelPricingIssues(
  issues: MaintenanceContractIssue[],
  level: MaintenanceSlaDraftLevel,
) {
  if (level.pricingCombinations.length === 0) {
    issues.push({ code: 'pricingCombination', levelCode: level.code });
    return;
  }

  for (const combo of level.pricingCombinations) {
    if (combo.priceAmount == null) {
      issues.push({ code: 'pricingCombinationPrice', levelCode: level.code });
    }
  }
}

export function collectMaintenanceContractIssues(
  draft: MaintenanceSlaDraft,
  scopeAssetHierarchy: MaintenanceSlaScopeHierarchyAsset[] = [],
): MaintenanceContractIssue[] {
  const issues: MaintenanceContractIssue[] = [];

  if (minutesOutOfRange(draft.restoredAvailableMinutes)) {
    issues.push({ code: 'restoredMinutes' });
  }

  for (const level of draft.levels) {
    collectLevelTimerIssues(issues, level);
    collectLevelPricingIssues(issues, level);
  }

  for (const rule of draft.scopeRules) {
    if (blankScopeValueBlocksSave(rule)) {
      issues.push({ code: 'scopeValue', ruleId: rule.id });
    }
  }

  if (scopeAssetHierarchy.length > 0) {
    const redundancies = findScopeIncludeHierarchyRedundancies(
      draft.scopeRules,
      scopeAssetHierarchy,
      draft.interventionMode,
    );
    for (const redundancy of redundancies) {
      issues.push({
        code: 'scopeHierarchyRedundancy',
        ruleId: redundancy.narrowerRuleId,
      });
    }
  }

  const effectConflicts = findScopeIncludeExcludeConflicts(
    draft.scopeRules,
    draft.interventionMode,
  );
  for (const conflict of effectConflicts) {
    issues.push({
      code: 'scopeEffectConflict',
      ruleId: conflict.includeRuleId,
    });
    issues.push({
      code: 'scopeEffectConflict',
      ruleId: conflict.excludeRuleId,
    });
  }

  return issues;
}

function sameIssueField<T>(left: T | undefined, right: T | undefined): boolean {
  return left === undefined || left === right;
}

export function hasMaintenanceIssue(
  issues: readonly MaintenanceContractIssue[],
  code: MaintenanceContractIssueCode,
  match?: Partial<MaintenanceContractIssue>,
): boolean {
  return issues.some((issue) => {
    if (issue.code !== code) {
      return false;
    }

    if (!match) {
      return true;
    }

    return (
      sameIssueField(match.levelCode, issue.levelCode) &&
      sameIssueField(match.kind, issue.kind) &&
      sameIssueField(match.ruleId, issue.ruleId)
    );
  });
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isClockStart(value: string | null): value is MaintenanceSlaClockStart {
  return (
    value != null &&
    (MAINTENANCE_SLA_CLOCK_STARTS as readonly string[]).includes(value)
  );
}

function isCalendarMode(
  value: string | null,
): value is MaintenanceSlaCalendarMode {
  return (
    value != null &&
    (MAINTENANCE_SLA_CALENDAR_MODES as readonly string[]).includes(value)
  );
}

function timersToSaved(
  timers: MaintenanceSlaDraftTimer[],
  rulesStatus: MaintenanceRulesStatus,
): MaintenanceSlaSavedTimer[] {
  const saved: MaintenanceSlaSavedTimer[] = [];

  for (const timer of timers) {
    if (!timer.included) {
      continue;
    }

    if (rulesStatus === 'DRAFT') {
      saved.push({
        kind: timer.kind,
        clockStart: timer.clockStart,
        calendarMode: timer.calendarMode,
      });
      continue;
    }

    if (
      !isClockStart(timer.clockStart) ||
      !isCalendarMode(timer.calendarMode)
    ) {
      continue;
    }

    saved.push({
      kind: timer.kind,
      clockStart: timer.clockStart,
      calendarMode: timer.calendarMode,
    });
  }

  return saved;
}

function pricingCombinationsToSaved(
  combinations: MaintenanceSlaDraftPricingCombination[],
  rulesStatus: MaintenanceRulesStatus,
): MaintenanceSlaSavedPricingCombination[] {
  if (rulesStatus === 'DRAFT') {
    return combinations.map((combo) => ({
      id: combo.id,
      takeChargeMinutes: combo.takeChargeMinutes,
      interventionMinutes: combo.interventionMinutes,
      resolutionMinutes: combo.resolutionMinutes,
      priceAmount: combo.priceAmount,
    }));
  }

  return combinations
    .filter((combo) => combo.priceAmount != null)
    .map((combo) => ({
      id: combo.id,
      takeChargeMinutes: combo.takeChargeMinutes ?? 0,
      interventionMinutes: combo.interventionMinutes ?? 0,
      resolutionMinutes: combo.resolutionMinutes ?? 0,
      priceAmount: combo.priceAmount ?? 0,
    }));
}

function levelToSaved(
  level: MaintenanceSlaDraftLevel,
  rulesStatus: MaintenanceRulesStatus,
): MaintenanceSlaSavedLevel {
  return {
    code: level.code,
    penaltyClauseText: emptyToNull(level.penaltyClauseText),
    penaltyAmount: level.penaltyAmount,
    timers: timersToSaved(level.timers, rulesStatus),
    pricingCombinations: pricingCombinationsToSaved(
      level.pricingCombinations,
      rulesStatus,
    ),
  };
}

export function maintenanceDraftToRecord(
  relationshipId: string,
  draft: MaintenanceSlaDraft,
  partnership: MaintenancePartnershipValidity,
  rulesStatus: MaintenanceRulesStatus,
): MaintenanceSlaContractRecord {
  const applicableCodes = applicableLevelCodes(draft.interventionMode);
  const filteredLevels = draft.levels.filter((level) =>
    applicableCodes.includes(level.code),
  );

  return {
    relationshipId,
    status: partnership.status,
    rulesStatus,
    validFrom: toStoredDate(partnership.validFrom),
    validTo: partnership.validTo ? toStoredDate(partnership.validTo) : null,
    timezone: draft.timezone.trim() || 'Europe/Paris',
    businessHoursStart: draft.businessHoursStart,
    businessHoursEnd: draft.businessHoursEnd,
    workingDays: [...draft.workingDays].sort((left, right) => left - right),
    holidayCalendarCode: draft.holidayCalendarCode.trim() || 'FR',
    exclusionsText: emptyToNull(draft.exclusionsText),
    resolutionStopSignal: EVIMO_RESOLUTION_STOP_SIGNAL,
    recurrenceWindowDays: draft.recurrenceWindowDays,
    restoredAvailableMinutes: durationPartsToMinutes(
      draft.restoredAvailableHours,
      draft.restoredAvailableMinutes,
    ),
    priceCurrency: draft.priceCurrency || 'EUR',
    excludeHolidays: draft.excludeHolidays,
    interventionMode: draft.interventionMode,
    levels: filteredLevels.map((level) => levelToSaved(level, rulesStatus)),
    scopeRules: draft.scopeRules.flatMap((rule) => {
      const seen = new Set<string>();
      const unique = rule.values
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .filter((v) => {
          if (seen.has(v)) return false;
          seen.add(v);
          return true;
        });
      return unique.map((value) => ({
        id: `${rule.id}-${value}`,
        dimension: rule.dimension,
        effect: rule.effect,
        value: rulesStatus === 'DRAFT' ? value : value,
        interventionMode: normalizeScopeRuleInterventionMode(
          rule,
          draft.interventionMode,
        ),
      }));
    }),
  };
}

function timersFromSaved(
  saved: MaintenanceSlaSavedTimer[],
): MaintenanceSlaDraftTimer[] {
  return createEmptyTimers().map((row) => {
    const match = saved.find((timer) => timer.kind === row.kind);

    if (!match) {
      return { ...row, included: false };
    }

    return {
      ...row,
      included: true,
      clockStart: match.clockStart,
      calendarMode: match.calendarMode,
    };
  });
}

function pricingCombinationsFromSaved(
  saved: MaintenanceSlaSavedPricingCombination[],
): MaintenanceSlaDraftPricingCombination[] {
  return saved.map((combo) => ({
    id: combo.id,
    takeChargeMinutes: combo.takeChargeMinutes,
    interventionMinutes: combo.interventionMinutes,
    resolutionMinutes: combo.resolutionMinutes,
    priceAmount: combo.priceAmount,
  }));
}

function levelFromSaved(
  code: MaintenanceSlaLevelCode,
  saved: MaintenanceSlaSavedLevel | undefined,
): MaintenanceSlaDraftLevel {
  const empty = createEmptyLevel(code);

  if (!saved) {
    return empty;
  }

  return {
    ...empty,
    penaltyClauseText: saved.penaltyClauseText ?? '',
    penaltyAmount: saved.penaltyAmount,
    timers: timersFromSaved(saved.timers),
    pricingCombinations: pricingCombinationsFromSaved(
      saved.pricingCombinations ?? [],
    ),
  };
}

export function maintenanceRecordToDraft(
  record: MaintenanceSlaContractRecord,
): MaintenanceSlaDraft {
  const restored = record.restoredAvailableMinutes;
  const mode = record.interventionMode ?? 'BOTH';

  const baseLevels = createFixedLevelsForInterventionMode(mode);
  const mergedLevels = baseLevels.map((baseLevel) => {
    const savedLevel = record.levels.find(
      (level) => level.code === baseLevel.code,
    );
    if (!savedLevel) {
      return baseLevel;
    }
    return levelFromSaved(baseLevel.code, savedLevel);
  });

  return {
    timezone: record.timezone || 'Europe/Paris',
    businessHoursStart: record.businessHoursStart,
    businessHoursEnd: record.businessHoursEnd,
    workingDays: [...record.workingDays],
    holidayCalendarCode: record.holidayCalendarCode || 'FR',
    exclusionsText: record.exclusionsText ?? '',
    recurrenceWindowDays: record.recurrenceWindowDays,
    restoredAvailableHours:
      restored == null ? null : minutesToDurationParts(restored).hours,
    restoredAvailableMinutes:
      restored == null ? null : minutesToDurationParts(restored).minutes,
    priceCurrency: record.priceCurrency || 'EUR',
    excludeHolidays: record.excludeHolidays ?? false,
    interventionMode: mode,
    levels: mergedLevels,
    scopeRules: (() => {
      const groups = new Map<
        string,
        {
          id: string;
          dimension: (typeof record.scopeRules)[number]['dimension'];
          effect: (typeof record.scopeRules)[number]['effect'];
          values: string[];
          interventionMode: MaintenanceSlaScopeInterventionMode | null;
        }
      >();
      for (const rule of record.scopeRules) {
        const value = rule.value ?? '';
        if (!value) continue;
        const interventionMode = rule.interventionMode ?? null;
        const key = `${rule.dimension}::${rule.effect}::${interventionMode ?? ''}`;
        const existing = groups.get(key);
        if (existing) {
          if (!existing.values.includes(value)) {
            existing.values.push(value);
          }
        } else {
          groups.set(key, {
            id: rule.id,
            dimension: rule.dimension,
            effect: rule.effect,
            values: [value],
            interventionMode,
          });
        }
      }
      return Array.from(groups.values()) as MaintenanceSlaDraftScopeRule[];
    })(),
  };
}
